import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import MainLayout from "../components/layout/MainLayout";
import { Product } from "../types/product";
import { LoadingComp } from "../components/common/loading.comp";
import { getProducts } from "../core/ship.core";
import ChevronRightIcon from "../icons/chevronRight.icon";
import profileCore from "../core/profile.core";
import { useTranslation } from "../core/translation.core";
import { ProductGridSection } from "../components/common/productGridSection.comp";

interface AttributeOption {
  value: string;
  count: number;
}

interface BrandOption {
  name: string;
  count: number;
  code: string;
}

interface FilterState {
  selectedAttributes: string[];
  selectedBrands: string[];
}

export default function ProductPage() {
  return (
    <MainLayout title="慈善專區 | Products" url="/products">
      <Main />
    </MainLayout>
  );
}

// Header component for the charity page
const ProductsHeader = () => {
  return (
    <div className="w-full bg-guru-floral-white border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-2xl md:text-3xl font-bold text-neutral-900">本地薑 瓹窿瓹罅 GuruGuru</h1>
      </div>
    </div>
  );
};

function Main() {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [wishlistedItems, setWishlistedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);
  const [filters, setFilters] = useState<FilterState>({
    selectedAttributes: [],
    selectedBrands: [],
  });
  const [showAttributtes, setShowAttributes] = useState(false);
  const [showBrands, setShowBrands] = useState(false);
  const { isReady } = router;

  const toggleShow = (type: string) => {
    type === "attributes" ? setShowAttributes((prev) => !prev) : setShowBrands((prev2) => !prev2);
  };

  const {
    data: giftCardsTypesIDs,
    isError: typeError,
    isPending: tyePending,
  } = useQuery({
    queryKey: ["giftCardsTypesIDs"],
    queryFn: () => profileCore.getGiftCardsTypeIDs("guru-default"),
    enabled: isReady,
  });

  const {
    data: productsData,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["productListOptions", filters],
    queryFn: () =>
      getProducts({
        size: 30,
        brands: filters.selectedBrands,
        getAttributeOptions: true,
        getBrandOptions: true,
        attributes: filters.selectedAttributes,
      }),
    // enabled: router.isReady,
  });

  const handleAttributeChange = (attribute: string) => {
    setFilters((prev) => ({
      ...prev,
      selectedAttributes: prev.selectedAttributes.includes(attribute)
        ? prev.selectedAttributes.filter((a) => a !== attribute)
        : [...prev.selectedAttributes, attribute],
    }));
  };

  const handleBrandChange = (brand: string) => {
    setFilters((prev) => ({
      ...prev,
      selectedBrands: prev.selectedBrands.includes(brand)
        ? prev.selectedBrands.filter((b) => b !== brand)
        : [...prev.selectedBrands, brand],
    }));
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Function to update wishlist items
  const updateWishlistItem = (productName: string, wishlistStatus: boolean) => {
    const newWishlisted = new Set(wishlistedItems);
    if (wishlistStatus) {
      newWishlisted.add(productName);
      setSuccessMessage(`已加入"${productName}"到願望清單`);
    } else {
      newWishlisted.delete(productName);
      setSuccessMessage(`已從願望清單移除"${productName}"`);
    }
    setWishlistedItems(newWishlisted);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Get products and total from query data
  const totalItems = productsData?.size ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Calculate pagination display info
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endItem = Math.min(startIdx + itemsPerPage, totalItems);
  const startItem = totalItems > 0 ? startIdx + 1 : 0;

  return (
    <div className="min-h-screen bg-guru-floral-white">
      {/* Header Banner */}
      <ProductsHeader />

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-20 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
          {successMessage}
        </div>
      )}

      {/* Products Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <h2 className="text-xl md:text-2xl font-semibold mb-6">商店熱賣</h2>

        {isPending ? (
          <LoadingComp padding="py-8" />
        ) : isError ? (
          <div className="text-center py-4 text-red-500">Error: {error.message}</div>
        ) : productsData && productsData.list.length > 0 ? (
          <div className="flex">
            {/* Filter sidebar */}
            <div className="w-64 p-4">
              <div className="mb-2">
                <div className="flex  justify-between items-center cursor-pointer mb-2" onClick={() => toggleShow("attributes")}>
                  <h3 className="font-semibold">Attributes</h3>
                  <ChevronRightIcon className={`stroke-[2] w-4 h-4   ${showAttributtes ? "rotate-90" : ""}`} />
                </div>
                <div className={`${showAttributtes ? "max-h-auto" : "max-h-0 leading-[0px] p-0 overflow-hidden"}`}>
                  {productsData.attributeOptions.map((option, i) => (
                    <label key={i} className={`flex items-center mb-2`}>
                      <input
                        type="checkbox"
                        checked={filters.selectedAttributes.includes(option.value)}
                        onChange={() => handleAttributeChange(option.value)}
                        className="mr-2"
                      />
                      {option.value} ({option.count})
                    </label>
                  ))}
                </div>
              </div>
              <div className="mb-2">
                <div className="flex justify-between items-center cursor-pointer mb-2" onClick={() => toggleShow("brands")}>
                  <h3 className="font-semibold ">Brands</h3>
                  <ChevronRightIcon className={`stroke-[2] w-4 h-4   ${showBrands ? "rotate-90" : ""}`} />
                </div>
                <div className={`${showBrands ? "max-h-auto" : "max-h-0 leading-[0px] p-0 overflow-hidden"}`}>
                  {productsData.brandOptions.map((brand, i) => (
                    <label key={i} className={`flex items-center mb-2`}>
                      <input
                        type="checkbox"
                        checked={filters.selectedBrands.includes(brand.name)}
                        onChange={() => handleBrandChange(brand.name)}
                        className="mr-2"
                      />
                      {brand.name} ({brand.count})
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Product grid */}
            <ProductGridSection
              products={productsData.list}
              wishlistedItems={wishlistedItems}
              updateWishlistItem={updateWishlistItem}
              giftCardsTypesIDs={giftCardsTypesIDs}
            />
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">{t(msg.product.noProductsAvailable)}</div>
        )}
      </div>
    </div>
  );
}
